import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Search, Loader2 } from "lucide-react";
import { api, regCode } from "../lib/api";

export default function Verify() {
  const { registryId } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(registryId || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (id) => {
    if (!id) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.get(`/verify/${id.trim()}`);
      setResult(data);
    } catch {
      setResult({ verified: false, message: "Verification failed. Please check the identifier." });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (registryId) run(registryId); }, [registryId]);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/verify/${input.trim().toUpperCase()}`);
    run(input);
  };

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-10 py-24" data-testid="verify-page">
      <div className="hy-label">Verification</div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-5">Verify a registry record</h1>
      <p className="text-sm text-slate-400 mt-6 max-w-2xl leading-relaxed">
        Enter a HYLUNER Registry ID (for example HYL-A1B2C3D4E5) or scan the QR code printed on a certificate.
      </p>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 mt-10" data-testid="verify-form">
        <input value={input} onChange={(e) => setInput(e.target.value)} className="hy-input" placeholder="HYL-XXXXXXXXXX" data-testid="verify-input" />
        <button className="hy-btn px-8 py-4 text-xs uppercase flex items-center justify-center gap-2" data-testid="verify-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Verify
        </button>
      </form>

      {result && (
        <div className={`hy-glass p-8 mt-12 ${result.verified ? "border-emerald-500/40" : "border-red-500/40"}`} data-testid="verify-result">
          <div className="flex items-center gap-3">
            {result.verified ? <CheckCircle2 className="w-6 h-6 text-emerald-400" /> : <XCircle className="w-6 h-6 text-red-400" />}
            <span className={`font-display text-xl ${result.verified ? "text-emerald-300" : "text-red-300"}`} data-testid="verify-status">
              {result.verified ? "Verified HYLUNER registry record" : "Not found"}
            </span>
          </div>

          {result.verified ? (
            <>
              <div className="hy-rule my-7" />
              <div className="grid sm:grid-cols-2 gap-6 text-sm">
                <Row k="Registry" v={regCode(result.record.registry_number)} />
                <Row k="Edition" v={`${result.record.edition} EDITION`} />
                <Row k="Type" v={result.record.registry_type} />
                <Row k="Dedication" v={result.record.dedication_name} />
                <Row k="Registry ID" v={result.record.registry_id} />
                <Row k="Certificate ID" v={result.record.certificate_id} />
                <Row k="Location" v={result.record.location_display} />
                <Row k="Registered" v={(result.record.registration_date || "").slice(0, 10)} />
              </div>
              <p className="text-[0.7rem] text-slate-500 mt-8 leading-relaxed">{result.disclosure}</p>
              <Link to={`/registry/${result.record.registry_id}`} className="hy-btn-ghost inline-block px-7 py-3 text-xs uppercase mt-7" data-testid="verify-view-record">
                View public registry page
              </Link>
            </>
          ) : (
            <p className="text-sm text-slate-400 mt-5">{result.message}</p>
          )}
        </div>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div>
    <div className="hy-label text-[0.55rem]">{k}</div>
    <div className="font-mono-hy text-amber-100/90 mt-1 break-all">{v}</div>
  </div>
);
